import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import NavBar from '@/components/layout/NavBar';
import Footer from '@/components/layout/Footer';
import SEO from '@/components/seo/SEO';
import GlassCard from '@/components/ui/GlassCard';
import { Shield, UserCheck, Users, FileCheck, Calendar, Lock, ShieldCheck, Smartphone, Database, FileText as FileTextIcon, BarChart, CircleCheck } from 'lucide-react';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import FadeInSection from '@/components/ui/FadeInSection';

const Solutions = () => {
  const location = useLocation();
  const currentPath = location.pathname;

  // Determine which solution to render based on the URL
  const renderContent = () => {
    if (currentPath === '/solutions/healthcare') {
      return <HealthcareSolution />;
    } else if (currentPath === '/solutions/retail') {
      return <RetailSolution />;
    } else if (currentPath === '/solutions/insurance') {
      return <InsuranceSolution />;
    } else {
      return <SolutionsMain />;
    }
  };

  return (
    <>
      <SEO 
        title={`${currentPath === '/solutions' ? 'Solutions' : 'Industry Solutions'} | Keystle`}
        description="Explore Keystle's identity and access management solutions for various sectors including healthcare, retail, and insurance."
      />
      <NavBar />
      <div className="pt-24 md:pt-28 min-h-[70vh]">
        {renderContent()}
      </div>
      <Footer />
    </>
  );
};

const SolutionsMain = () => (
  <div className="container mx-auto px-6">
    <h1 className="text-4xl font-display font-bold mb-8 mt-12">Solutions</h1>
    
    <div className="grid grid-cols-1 gap-8 md:grid-cols-2 lg:grid-cols-3 mb-12">
      <Link to="/solutions/healthcare" className="block">
        <GlassCard className="h-full transition-all hover:shadow-md hover:border-brand-pink/40" hoverEffect={true}>
          <div className="flex items-center gap-3 mb-4">
            <div className="p-2 rounded-full bg-pink-50 dark:bg-pink-900/20">
              <UserCheck className="h-6 w-6 text-[#D62D82]" />
            </div>
            <h2 className="text-2xl font-bold">Healthcare</h2>
          </div>
          <p className="text-gray-700 dark:text-gray-300 mb-4">Simplify access and increase compliance with our healthcare identity solutions.</p>
          <div className="text-brand-pink hover:text-brand-pink/90 inline-flex items-center gap-1">
            Learn more <span aria-hidden="true">→</span>
          </div>
        </GlassCard>
      </Link>
      
      <Link to="/solutions/retail" className="block">
        <GlassCard className="h-full transition-all hover:shadow-md hover:border-brand-pink/40" hoverEffect={true}>
          <div className="flex items-center gap-3 mb-4">
            <div className="p-2 rounded-full bg-pink-50 dark:bg-pink-900/20">
              <Users className="h-6 w-6 text-[#D62D82]" />
            </div>
            <h2 className="text-2xl font-bold">Retail &amp; SaaS</h2>
          </div>
          <p className="text-gray-700 dark:text-gray-300 mb-4">Scale with secure identity and passwordless solutions for B2B and SaaS platforms.</p>
          <div className="text-brand-pink hover:text-brand-pink/90 inline-flex items-center gap-1">
            Learn more <span aria-hidden="true">→</span>
          </div>
        </GlassCard>
      </Link>
      
      <Link to="/solutions/insurance" className="block">
        <GlassCard className="h-full transition-all hover:shadow-md hover:border-brand-pink/40" hoverEffect={true}>
          <div className="flex items-center gap-3 mb-4">
            <div className="p-2 rounded-full bg-pink-50 dark:bg-pink-900/20">
              <Shield className="h-6 w-6 text-[#D62D82]" />
            </div>
            <h2 className="text-2xl font-bold">Insurance</h2>
          </div>
          <p className="text-gray-700 dark:text-gray-300 mb-4">Digitalize operations with secure identity and passwordless customer journeys.</p>
          <div className="text-brand-pink hover:text-brand-pink/90 inline-flex items-center gap-1">
            Learn more <span aria-hidden="true">→</span>
          </div>
        </GlassCard>
      </Link>
    </div>
  </div>
);

const HealthcareSolution = () => {
  const stats = [
    {
      title: "$10.1 million",
      description: "Average cost of a data breach in the healthcare industry",
      source: "Ponemon Institute, 2024"
    },
    {
      title: "81%",
      description: "Healthcare data breaches caused by authentication failures",
      source: "Verizon DBIR 2023"
    },
    {
      title: "99.9%",
      description: "Reduction in breach risk with MFA implementation",
      source: "Microsoft"
    }
  ];

  const useCases = [
    {
      icon: <UserCheck className="h-8 w-8 text-[#D62D82]" />,
      title: "Improve clinical experience",
      description: "Keystle helps hospitals, clinics, and healthcare providers ensure fast, secure access to electronic medical records, prescription systems, and internal environments. Eliminate passwords with biometric authentication and adaptive MFA."
    },
    {
      icon: <FileCheck className="h-8 w-8 text-[#D62D82]" />,
      title: "Deliver intuitive patient portals",
      description: "Implement passwordless access and consent management with automated logs. Patients can schedule appointments, view exams, and share data with their doctors with complete security, in compliance with LGPD and ANS requirements."
    },
    {
      icon: <Shield className="h-8 w-8 text-[#D62D82]" />,
      title: "Protect sensitive data",
      description: "Use AI to detect and block fraudulent login attempts, anomalous access, or suspicious registrations. Keystle analyzes behavior and device patterns in real time to prevent leaks and fraud before they happen."
    },
    {
      icon: <Calendar className="h-8 w-8 text-[#D62D82]" />,
      title: "Improve compliance and simplify audits",
      description: "Implement complete audit trails for access, consents and sensitive activities. Keystle automates the recording and generation of reports that are compliant with standards such as LGPD, HIPAA and ANS."
    },
    {
      icon: <Lock className="h-8 w-8 text-[#D62D82]" />,
      title: "Eliminate shared credentials risk",
      description: "Replace traditional passwords with strong authentication via biometrics, passkeys and trusted devices. Keystle detects suspicious simultaneous logins and blocks anomalous access automatically."
    },
    {
      icon: <Users className="h-8 w-8 text-[#D62D82]" />,
      title: "Secure information sharing",
      description: "Protect the flow of clinical information between doctors, nurses and administrators with authentication via trusted devices and context-based access policies."
    }
  ];

  return (
    <div className="container mx-auto px-6">
      <div className="mb-8">
        <Link to="/solutions" className="text-sm text-brand-pink hover:text-brand-pink/90 mb-6 inline-flex items-center gap-1">
          <span aria-hidden="true">←</span> All Solutions
        </Link>
        <div className="flex flex-col md:flex-row md:items-center gap-6 mb-8">
          <div className="md:flex-1">
            <Badge variant="secondary" className="mb-4">Healthcare</Badge>
            <h1 className="text-4xl md:text-5xl font-display font-bold mb-6">Simplify Access and Increase Compliance</h1>
            <p className="text-lg text-gray-700 dark:text-gray-300 mb-6">
              Secure healthcare data while improving the experience for clinicians and patients.
            </p>
            <Button className="bg-[#843868] hover:bg-[#843868]/90 text-white">
              Request a Demo
            </Button>
          </div>
          <div className="md:flex-1">
            <img 
              src="/lovable-uploads/fa1db265-400a-403c-aac6-5e5fddece376.png" 
              alt="Healthcare professional using a laptop with stethoscope" 
              className="rounded-lg shadow-lg w-full max-h-80 object-cover" 
            />
          </div>
        </div>
      </div>

      <section className="mb-16 bg-[#d62d82]/5 rounded-lg p-8">
        <h2 className="text-3xl font-bold mb-8 text-center">Industry Statistics</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {stats.map((stat, index) => (
            <Card key={index} className="border border-gray-200 dark:border-gray-800 hover:shadow-md transition-all">
              <CardHeader className="pb-2 text-center">
                <CardTitle className="text-3xl font-bold text-[#d62d82] text-center">{stat.title}</CardTitle>
                <CardDescription className="text-base text-center">{stat.description}</CardDescription>
              </CardHeader>
              <CardContent className="text-center">
                <p className="text-sm text-gray-500">Source: {stat.source}</p>
              </CardContent>
            </Card>
          ))}
        </div>
      </section>

      <section className="mb-16">
        <h2 className="text-3xl font-bold mb-8">Healthcare Use Cases</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {useCases.map((useCase, index) => (
            <GlassCard key={index} className="flex flex-col h-full">
              <div className="mb-4 p-2 rounded-full bg-pink-50 dark:bg-pink-900/20 inline-block w-12 h-12 flex items-center justify-center shadow-sm">
                {useCase.icon}
              </div>
              <h3 className="text-xl font-bold mb-3">{useCase.title}</h3>
              <p className="text-gray-700 dark:text-gray-300">{useCase.description}</p>
            </GlassCard>
          ))}
        </div>
      </section>

      <section className="mb-16">
        <h2 className="text-3xl font-bold mb-8">Frequently Asked Questions</h2>
        <div className="border border-gray-200 dark:border-gray-800 rounded-lg p-4 md:p-6">
          <Accordion type="single" collapsible className="w-full">
            <AccordionItem value="item-1">
              <AccordionTrigger>How does Keystle ensure HIPAA compliance?</AccordionTrigger>
              <AccordionContent>
                Keystle provides complete audit trails, strong authentication mechanisms, and encrypted data transmission that aligns with HIPAA requirements. Our platform helps maintain the confidentiality, integrity, and availability of protected health information.
              </AccordionContent>
            </AccordionItem>
            <AccordionItem value="item-2">
              <AccordionTrigger>Can Keystle integrate with existing EMR systems?</AccordionTrigger>
              <AccordionContent>
                Yes, Keystle offers seamless integration with popular Electronic Medical Record systems through standard protocols such as SAML, OAuth, and OpenID Connect, ensuring a smooth authentication experience without disrupting existing workflows.
              </AccordionContent>
            </AccordionItem>
            <AccordionItem value="item-3">
              <AccordionTrigger>How does the biometric authentication work?</AccordionTrigger>
              <AccordionContent>
                Keystle leverages device-native biometric capabilities (fingerprint, facial recognition) to provide seamless and secure authentication. Biometric data never leaves the user's device, maintaining privacy while providing strong security.
              </AccordionContent>
            </AccordionItem>
          </Accordion>
        </div>
      </section>

      <section className="mb-16 bg-[#d62d82]/95 rounded-lg p-8">
        <div className="flex flex-col md:flex-row items-center justify-between gap-8">
          <div>
            <h2 className="text-2xl md:text-3xl font-bold mb-4 text-white">Ready to secure your healthcare environment?</h2>
            <p className="text-white/90 mb-6">Contact our team to schedule a personalized demo.</p>
          </div>
          <Button className="bg-white hover:bg-white/90 text-[#d62d82]">
            Request a Demo
          </Button>
        </div>
      </section>
    </div>
  );
};

const RetailSolution = () => {
  const stats = [
    {
      title: "70%",
      description: "Companies with MFA report 99.9% reduction in intrusion attempts",
      source: "Microsoft"
    },
    {
      title: "$9.36 million",
      description: "Average cost of a data breach for technology companies",
      source: "IBM, 2024"
    },
    {
      title: "400%",
      description: "Increase in passwordless authentication adoption",
      source: "The Verge, 2024"
    }
  ];

  const useCases = [
    {
      icon: <Users className="h-8 w-8 text-[#D62D82]" />,
      title: "Unify enterprise access with SSO and intelligent MFA",
      description: "Manage employee identities and permissions with a single platform. Keystle offers unified sign-on with SAML, OAuth, and OIDC support, plus adaptive MFA with context rules for frictionless security."
    },
    {
      icon: <Database className="h-8 w-8 text-[#D62D82]" />,
      title: "Build multitenant environments with identity governance",
      description: "For SaaS platforms, offer customer-based authentication and authorization with RBAC, ABAC, or PBAC controls. Keystle lets you segregate access by tenant with secure APIs and full CI/CD integration support."
    },
    {
      icon: <Smartphone className="h-8 w-8 text-[#D62D82]" />,
      title: "Reduce abandonment and improve conversion with invisible authentication",
      description: "Increase engagement with biometric, magic link, or QR code login. With Keystle, your users sign in seamlessly, without passwords or forms. Ideal for progressive onboarding and omnichannel journeys."
    },
    {
      icon: <ShieldCheck className="h-8 w-8 text-[#D62D82]" />,
      title: "Real-time threat detection and automated auditing",
      description: "Monitor risks with Keystle Risk Layer: risk score, device fingerprint, anomalous behavior blocking, and full session auditing. Reduce fraud and ensure compliance with LGPD, SOC 2, and ISO."
    },
    {
      icon: <FileTextIcon className="h-8 w-8 text-[#D62D82]" />,
      title: "Automate audits and ensure compliance with global standards",
      description: "Simplify audit and governance processes with automatic logs and inspection-ready compliance reports. Keystle helps your company comply with regulations such as LGPD, SOC 2, and ISO 27001, strengthening the trust of the market and your customers."
    }
  ];

  return (
    <div className="container mx-auto px-6">
      <div className="mb-12">
        <Link to="/solutions" className="text-sm text-brand-pink hover:text-brand-pink/90 mb-6 inline-flex items-center gap-1">
          <span aria-hidden="true">←</span> All Solutions
        </Link>
        
        <FadeInSection>
          <div className="flex flex-col md:flex-row md:items-center gap-6 mb-12">
            <div className="md:flex-1">
              <Badge variant="secondary" className="mb-4">B2B Retail & SaaS</Badge>
              <h1 className="text-4xl md:text-5xl font-display font-bold mb-6">Scale with Secure, Passwordless Identity</h1>
              <p className="text-lg text-gray-700 dark:text-gray-300 mb-6">
                Empower your retail and SaaS businesses with seamless, secure identity solutions that scale with your growth.
              </p>
              <Button className="bg-[#843868] hover:bg-[#843868]/90 text-white">
                Request a Demo
              </Button>
            </div>
            <div className="md:flex-1">
              <img 
                src="/lovable-uploads/df4bba18-de4d-4ca0-b51c-8a78b68c0116.png" 
                alt="Digital shopping cart hologram on tablet - modern retail e-commerce concept" 
                className="rounded-lg shadow-lg w-full max-h-80 object-cover" 
              />
            </div>
          </div>
        </FadeInSection>
      </div>

      <section className="mb-16 bg-[#d62d82]/5 rounded-lg p-8">
        <FadeInSection>
          <h2 className="text-3xl font-bold mb-8">Industry Statistics</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {stats.map((stat, index) => (
              <Card key={index} className="border border-gray-200 dark:border-gray-800 hover:border-purple-300 dark:hover:border-purple-700 transition-all">
                <CardHeader className="pb-2 text-center">
                  <CardTitle className="text-3xl font-bold text-[#d62d82] text-center">{stat.title}</CardTitle>
                  <CardDescription className="text-base text-center">{stat.description}</CardDescription>
                </CardHeader>
                <CardContent className="text-center">
                  <p className="text-sm text-gray-500">Source: {stat.source}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </FadeInSection>
      </section>

      <section className="mb-16">
        <FadeInSection>
          <h2 className="text-3xl font-bold mb-8">Retail & SaaS Use Cases</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {useCases.map((useCase, index) => (
              <GlassCard key={index} className="flex flex-col h-full hover:shadow-md transition-all" hoverEffect={true}>
                <div className="mb-4 p-2 rounded-full bg-pink-50 dark:bg-pink-900/20 inline-block w-12 h-12 flex items-center justify-center shadow-sm">
                  {useCase.icon}
                </div>
                <h3 className="text-xl font-bold mb-3">{useCase.title}</h3>
                <p className="text-gray-700 dark:text-gray-300">{useCase.description}</p>
              </GlassCard>
            ))}
          </div>
        </FadeInSection>
      </section>

      <section className="mb-16">
        <FadeInSection>
          <h2 className="text-3xl font-bold mb-8">Frequently Asked Questions</h2>
          <div className="border border-gray-200 dark:border-gray-800 rounded-lg p-4 md:p-6">
            <Accordion type="single" collapsible className="w-full">
              <AccordionItem value="item-1">
                <AccordionTrigger>How does Keystle help with multi-tenant SaaS platforms?</AccordionTrigger>
                <AccordionContent>
                  Keystle provides robust tenant isolation capabilities that allow you to segregate customer data and access controls. Our platform supports custom branding per tenant, granular RBAC/ABAC policies, and API-driven identity management that integrates seamlessly with your existing infrastructure.
                </AccordionContent>
              </AccordionItem>
              <AccordionItem value="item-2">
                <AccordionTrigger>Can Keystle integrate with our existing e-commerce platform?</AccordionTrigger>
                <AccordionContent>
                  Yes, Keystle offers seamless integration with popular e-commerce platforms through standard protocols such as SAML, OAuth, and OpenID Connect. We also provide custom SDKs and APIs for specialized integration needs, ensuring a smooth authentication experience without disrupting your existing customer journey.
                </AccordionContent>
              </AccordionItem>
              <AccordionItem value="item-3">
                <AccordionTrigger>How does passwordless authentication improve conversion rates?</AccordionTrigger>
                <AccordionContent>
                  Passwordless authentication removes friction from the sign-up and login processes, reducing cart abandonment and increasing conversion rates. Our clients typically see a 30% reduction in form abandonment and a 25% increase in customer retention when implementing our passwordless solutions like magic links, biometrics, or social authentication.
                </AccordionContent>
              </AccordionItem>
            </Accordion>
          </div>
        </FadeInSection>
      </section>

      <section className="mb-16 bg-[#d62d82]/95 rounded-lg p-8">
        <FadeInSection>
          <div className="flex flex-col md:flex-row items-center justify-between gap-8">
            <div>
              <h2 className="text-2xl md:text-3xl font-bold mb-4 text-white">Ready to transform your retail and SaaS identity?</h2>
              <p className="text-white/90 mb-6">Connect with our team for a personalized demo tailored to your business needs.</p>
            </div>
            <Button className="bg-white hover:bg-white/90 text-[#d62d82]">
              Request a Demo
            </Button>
          </div>
        </FadeInSection>
      </section>
    </div>
  );
};

const InsuranceSolution = () => {
  const stats = [
    {
      title: "$7 billion",
      description: "Losses caused by digital fraud in 2022, impacting customer and transaction security",
      source: "Business Research Insights, 2022"
    },
    {
      title: "50%",
      description: "Reduction in support costs with passwordless authentication",
      source: "The Verge"
    },
    {
      title: "30%",
      description: "Annual reduction in fraud costs with adaptive MFA",
      source: "Forrester Research"
    }
  ];

  const useCases = [
    {
      icon: <UserCheck className="h-8 w-8 text-[#D62D82]" />,
      title: "Accelerate Customer Onboarding with Invisible Authentication",
      description: "Keystle enables insurers to offer seamless onboarding using passwordless authentication via biometrics, magic links, or QR codes. Capture consent and verify identities without friction, increasing conversion from the first contact and reducing abandonment at enrollment."
    },
    {
      icon: <Shield className="h-8 w-8 text-[#D62D82]" />,
      title: "Secure self-service portals and claims apps with adaptive authentication",
      description: "Implement biometric authentication and adaptive MFA to secure customer portals and self-service apps. Use real-time risk analytics to automatically block suspicious activity, ensuring security without impacting legitimate users."
    },
    {
      icon: <Lock className="h-8 w-8 text-[#D62D82]" />,
      title: "Control internal access with intelligent governance",
      description: "Manage access permissions with RBAC, ABAC, and PBAC, ensuring each user accesses only the systems and data they need, based on their role, location, and risk context. Reduce internal risk and simplify audits."
    },
    {
      icon: <FileCheck className="h-8 w-8 text-[#D62D82]" />,
      title: "Ensure compliance with LGPD, ANS and international standards",
      description: "Audit access, record consents and apply security policies automatically. With Keystle, you can generate complete compliance reports ready for regulatory inspections, meeting the requirements of LGPD, ANS and global privacy standards."
    },
    {
      icon: <ShieldCheck className="h-8 w-8 text-[#D62D82]" />,
      title: "Eliminate the risks of shared credentials and internal fraud",
      description: "Replace passwords with strong individualized authentication, with biometrics and trusted devices (device trust). Detect simultaneous login attempts and block anomalous behaviors automatically, increasing the security of internal accesses."
    },
    {
      icon: <BarChart className="h-8 w-8 text-[#D62D82]" />,
      title: "Monitor the security of agents and devices in real time",
      description: "Keystle identifies suspicious sessions with Device Fingerprint and Risk Scoring, automatically blocking insecure accesses from compromised devices. Reduce incidents of unauthorized access by up to 60%, maintaining the integrity of sensitive data."
    }
  ];

  return (
    <div className="container mx-auto px-6">
      <div className="mb-12">
        <Link to="/solutions" className="text-sm text-brand-pink hover:text-brand-pink/90 mb-6 inline-flex items-center gap-1">
          <span aria-hidden="true">←</span> All Solutions
        </Link>
        
        <FadeInSection>
          <div className="flex flex-col md:flex-row md:items-center gap-6 mb-12">
            <div className="md:flex-1">
              <Badge variant="secondary" className="mb-4">Insurance</Badge>
              <h1 className="text-4xl md:text-5xl font-display font-bold mb-6">Digitize Operations with Secure Identity</h1>
              <p className="text-lg text-gray-700 dark:text-gray-300 mb-6">
                Streamline insurance operations with secure, passwordless customer journeys that protect sensitive data.
              </p>
              <Button className="bg-[#843868] hover:bg-[#843868]/90 text-white">
                Request a Demo
              </Button>
            </div>
            <div className="md:flex-1">
              <img 
                src="/lovable-uploads/5d48743b-eb18-4330-847e-22806e4df071.png" 
                alt="Insurance digital services with floating icons showing protection, healthcare, family, home and vehicle coverage" 
                className="rounded-lg shadow-lg w-full max-h-80 object-cover" 
              />
            </div>
          </div>
        </FadeInSection>
      </div>

      <section className="mb-16 bg-[#843868]/5 rounded-lg p-8">
        <FadeInSection>
          <h2 className="text-3xl font-bold mb-8">Industry Statistics</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {stats.map((stat, index) => (
              <Card key={index} className="border border-gray-200 dark:border-gray-800 hover:border-indigo-300 dark:hover:border-indigo-700 transition-all">
                <CardHeader className="pb-2 text-center">
                  <CardTitle className="text-3xl font-bold text-[#d62d82] text-center">{stat.title}</CardTitle>
                  <CardDescription className="text-base text-foreground text-center">{stat.description}</CardDescription>
                </CardHeader>
                <CardContent className="text-center">
                  <p className="text-sm text-gray-500">Source: {stat.source}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </FadeInSection>
      </section>

      <section className="mb-16">
        <FadeInSection>
          <h2 className="text-3xl font-bold mb-8">Insurance Use Cases</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {useCases.map((useCase, index) => (
              <GlassCard key={index} className="flex flex-col h-full hover:shadow-md transition-all" hoverEffect={true}>
                <div className="mb-4 p-2 rounded-full bg-pink-50 dark:bg-pink-900/20 inline-block w-12 h-12 flex items-center justify-center shadow-sm">
                  {useCase.icon}
                </div>
                <h3 className="text-xl font-bold mb-3">{useCase.title}</h3>
                <p className="text-gray-700 dark:text-gray-300">{useCase.description}</p>
              </GlassCard>
            ))}
          </div>
        </FadeInSection>
      </section>

      <section className="mb-16">
        <FadeInSection>
          <h2 className="text-3xl font-bold mb-8">Frequently Asked Questions</h2>
          <div className="border border-gray-200 dark:border-gray-800 rounded-lg p-4 md:p-6">
            <Accordion type="single" collapsible className="w-full">
              <AccordionItem value="item-1">
                <AccordionTrigger>How does Keystle help reduce insurance fraud?</AccordionTrigger>
                <AccordionContent>
                  Keystle employs multiple layers of security including biometric verification, device fingerprinting, and behavioral analytics to detect and prevent fraudulent activities. Our risk scoring system identifies suspicious patterns in real-time, blocking unauthorized access attempts before they can lead to data breaches or fraudulent claims.
                </AccordionContent>
              </AccordionItem>
              <AccordionItem value="item-2">
                <AccordionTrigger>Can Keystle integrate with our existing claims processing systems?</AccordionTrigger>
                <AccordionContent>
                  Yes, Keystle is designed to integrate seamlessly with your existing insurance systems through standard protocols such as SAML, OAuth, and OpenID Connect. Our APIs enable secure authentication for your claims processing, policy management, and customer portals without disrupting your established workflows.
                </AccordionContent>
              </AccordionItem>
              <AccordionItem value="item-3">
                <AccordionTrigger>How does passwordless authentication improve the customer experience?</AccordionTrigger>
                <AccordionContent>
                  Passwordless authentication eliminates the friction of remembering and entering passwords, significantly improving the customer experience. Customers can securely access their accounts using biometrics, magic links, or QR codes, resulting in faster login times, reduced abandonment rates, and increased customer satisfaction. Our clients typically see a 40% reduction in login-related support tickets after implementing passwordless solutions.
                </AccordionContent>
              </AccordionItem>
            </Accordion>
          </div>
        </FadeInSection>
      </section>

      <section className="mb-16 bg-[#d62d82]/95 rounded-lg p-8">
        <FadeInSection>
          <div className="flex flex-col md:flex-row items-center justify-between gap-8">
            <div>
              <h2 className="text-2xl md:text-3xl font-bold mb-4 text-white">Ready to transform your insurance identity security?</h2>
              <p className="text-white/90 mb-6">Connect with our team for a personalized demo tailored to your insurance operations.</p>
            </div>
            <Button className="bg-white hover:bg-white/90 text-[#d62d82]">
              Request a Demo
            </Button>
          </div>
        </FadeInSection>
      </section>
    </div>
  );
};

export default Solutions;
